import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { BarChart3, Users, Mail, Settings, LogOut, Eye, Trash2, RefreshCw, Loader2, Phone, CheckCircle2, Clock, XCircle, MessageSquare, Save, FileText, Code } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  new: "bg-blue-100 text-blue-800",
  contacted: "bg-yellow-100 text-yellow-800",
  quoted: "bg-purple-100 text-purple-800",
  won: "bg-green-100 text-green-800",
  lost: "bg-red-100 text-red-800",
};

function getAuthHeaders() {
  const token = localStorage.getItem("admin_token");
  return { Authorization: `Bearer ${token}` };
}

function StatsCards({ stats }) {
  const cards = [
    { label: "Total Leads", value: stats.total_leads, icon: Users, color: "#0B203B" },
    { label: "New Leads", value: stats.new_leads, icon: Clock, color: "#3B82F6" },
    { label: "Contacted", value: stats.contacted, icon: Phone, color: "#F59E0B" },
    { label: "Quoted", value: stats.quoted, icon: MessageSquare, color: "#8B5CF6" },
    { label: "Won", value: stats.won, icon: CheckCircle2, color: "#10B981" },
    { label: "Lost", value: stats.lost, icon: XCircle, color: "#EF4444" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
      {cards.map((c, i) => (
        <div key={i} className="bg-white rounded-xl border border-[#E2E8F0] p-4" data-testid={`stat-card-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
          <div className="flex items-center gap-2 mb-2">
            <c.icon className="w-4 h-4" style={{ color: c.color }} />
            <span className="text-xs text-[#64748B] font-medium">{c.label}</span>
          </div>
          <p className="text-2xl font-bold text-[#0B203B]">{c.value}</p>
        </div>
      ))}
    </div>
  );
}

function LeadDetailDialog({ lead }) {
  if (!lead) return null;
  const fields = [
    ["Name", lead.full_name], ["Email", lead.email], ["Phone", lead.phone],
    ["Service", lead.service_type], ["Type", lead.customer_type], ["Postcode", lead.postcode],
    ["Provider", lead.current_provider], ["Bill", lead.quarterly_bill ? `$${lead.quarterly_bill}` : "N/A"],
    ["Solar", lead.has_solar ? "Yes" : "No"], ["Contact Time", lead.preferred_contact_time || "Any"],
    ["Notes", lead.notes], ["Status", lead.status], ["Created", new Date(lead.created_at).toLocaleString()],
  ];
  return (
    <div className="space-y-3">
      {fields.map(([k, v]) => (
        <div key={k} className="flex gap-3 text-sm">
          <span className="font-semibold text-[#2E2E33] w-28 flex-shrink-0">{k}</span>
          <span className="text-[#64748B]">{v || "N/A"}</span>
        </div>
      ))}
    </div>
  );
}

function LeadsTable({ leads, onStatusChange, onDelete, onRefresh, loading }) {
  const [filter, setFilter] = useState("all");
  const filtered = filter === "all" ? leads : leads.filter((l) => l.status === filter);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Select onValueChange={setFilter} defaultValue="all">
          <SelectTrigger className="w-40" data-testid="leads-filter-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Leads</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="contacted">Contacted</SelectItem>
            <SelectItem value="quoted">Quoted</SelectItem>
            <SelectItem value="won">Won</SelectItem>
            <SelectItem value="lost">Lost</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="leads-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <span className="text-sm text-[#64748B]">{filtered.length} leads</span>
      </div>
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <Table data-testid="leads-table">
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead className="font-semibold">Name</TableHead>
              <TableHead className="font-semibold">Service</TableHead>
              <TableHead className="font-semibold">Type</TableHead>
              <TableHead className="font-semibold">Phone</TableHead>
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">Date</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center py-12 text-[#64748B]">No leads found</TableCell></TableRow>
            ) : (
              filtered.map((lead) => (
                <TableRow key={lead.id} data-testid={`lead-row-${lead.id}`}>
                  <TableCell>
                    <div>
                      <p className="font-medium text-[#0B203B] text-sm">{lead.full_name}</p>
                      <p className="text-xs text-[#64748B]">{lead.email}</p>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm">{lead.service_type}</TableCell>
                  <TableCell className="text-sm capitalize">{lead.customer_type}</TableCell>
                  <TableCell className="text-sm">{lead.phone}</TableCell>
                  <TableCell>
                    <Select onValueChange={(val) => onStatusChange(lead.id, val)} defaultValue={lead.status}>
                      <SelectTrigger className="h-8 w-28" data-testid={`lead-status-${lead.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["new", "contacted", "quoted", "won", "lost"].map((s) => (
                          <SelectItem key={s} value={s}><span className="capitalize">{s}</span></SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-xs text-[#64748B]">{new Date(lead.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button variant="ghost" size="sm" data-testid={`lead-view-${lead.id}`}><Eye className="w-4 h-4" /></Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-md">
                          <DialogHeader><DialogTitle>Lead Details</DialogTitle></DialogHeader>
                          <LeadDetailDialog lead={lead} />
                        </DialogContent>
                      </Dialog>
                      <Button variant="ghost" size="sm" onClick={() => onDelete(lead.id)} data-testid={`lead-delete-${lead.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ContactsTable({ contacts }) {
  return (
    <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
      <Table data-testid="contacts-table">
        <TableHeader>
          <TableRow className="bg-[#F8FAFC]">
            <TableHead className="font-semibold">Name</TableHead>
            <TableHead className="font-semibold">Email</TableHead>
            <TableHead className="font-semibold">Phone</TableHead>
            <TableHead className="font-semibold">Subject</TableHead>
            <TableHead className="font-semibold">Message</TableHead>
            <TableHead className="font-semibold">Date</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contacts.length === 0 ? (
            <TableRow><TableCell colSpan={6} className="text-center py-12 text-[#64748B]">No contacts yet</TableCell></TableRow>
          ) : (
            contacts.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium text-sm">{c.name}</TableCell>
                <TableCell className="text-sm">{c.email}</TableCell>
                <TableCell className="text-sm">{c.phone || "N/A"}</TableCell>
                <TableCell className="text-sm">{c.subject || "General"}</TableCell>
                <TableCell className="text-sm max-w-xs truncate">{c.message}</TableCell>
                <TableCell className="text-xs text-[#64748B]">{new Date(c.created_at).toLocaleDateString()}</TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

function EmailLogsPanel() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/email-logs`, { headers: getAuthHeaders() });
      setLogs(res.data);
    } catch { }
    setLoading(false);
  };

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <h3 className="font-bold text-[#0B203B] text-lg">Email Log</h3>
        <Button variant="outline" size="sm" onClick={loadLogs} disabled={loading} data-testid="email-logs-refresh">
          <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
        <span className="text-sm text-[#64748B]">{logs.length} emails</span>
      </div>
      <div className="bg-white rounded-xl border border-[#E2E8F0] overflow-hidden">
        <Table data-testid="email-logs-table">
          <TableHeader>
            <TableRow className="bg-[#F8FAFC]">
              <TableHead className="font-semibold">Status</TableHead>
              <TableHead className="font-semibold">To</TableHead>
              <TableHead className="font-semibold">Subject</TableHead>
              <TableHead className="font-semibold">Sent At</TableHead>
              <TableHead className="font-semibold text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {logs.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="text-center py-12 text-[#64748B]">{loading ? "Loading..." : "No emails sent yet"}</TableCell></TableRow>
            ) : (
              logs.map((log) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <Badge className={log.status === "sent" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"} data-testid={`email-status-${log.id}`}>
                      {log.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm">{log.to}</TableCell>
                  <TableCell className="text-sm max-w-xs truncate">{log.subject}</TableCell>
                  <TableCell className="text-xs text-[#64748B]">{new Date(log.sent_at).toLocaleString()}</TableCell>
                  <TableCell className="text-right">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedLog(log)} data-testid={`email-view-${log.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                        <DialogHeader><DialogTitle>Email Content</DialogTitle></DialogHeader>
                        {selectedLog && (
                          <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div><span className="font-semibold">To:</span> {selectedLog.to}</div>
                              <div><span className="font-semibold">Status:</span> <Badge className={selectedLog.status === "sent" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>{selectedLog.status}</Badge></div>
                              <div className="col-span-2"><span className="font-semibold">Subject:</span> {selectedLog.subject}</div>
                              {selectedLog.error && <div className="col-span-2 text-red-600"><span className="font-semibold">Error:</span> {selectedLog.error}</div>}
                            </div>
                            <div className="border border-[#E2E8F0] rounded-lg p-4 bg-white">
                              <p className="text-xs font-semibold text-[#64748B] mb-2">Email Body:</p>
                              <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: selectedLog.html }} />
                            </div>
                          </div>
                        )}
                      </DialogContent>
                    </Dialog>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function EmailTemplatesPanel() {
  const [templates, setTemplates] = useState(null);
  const [activeTemplate, setActiveTemplate] = useState("admin_notification");
  const [subject, setSubject] = useState("");
  const [htmlContent, setHtmlContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => { loadTemplates(); }, []);

  const loadTemplates = async () => {
    try {
      const res = await axios.get(`${API}/email-templates`, { headers: getAuthHeaders() });
      setTemplates(res.data);
      const tmpl = res.data[activeTemplate];
      if (tmpl) {
        setSubject(tmpl.subject);
        setHtmlContent(tmpl.html_content);
      }
    } catch { }
  };

  const switchTemplate = (type) => {
    setActiveTemplate(type);
    if (templates && templates[type]) {
      setSubject(templates[type].subject);
      setHtmlContent(templates[type].html_content);
    }
    setShowPreview(false);
  };

  const saveTemplate = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/email-templates`, {
        template_type: activeTemplate,
        subject,
        html_content: htmlContent,
      }, { headers: getAuthHeaders() });
      toast.success("Template saved!");
      setTemplates((prev) => ({ ...prev, [activeTemplate]: { subject, html_content: htmlContent } }));
    } catch {
      toast.error("Failed to save template");
    }
    setSaving(false);
  };

  const previewHtml = () => {
    const sampleData = {
      full_name: "John Smith",
      email: "john@example.com",
      phone: "0412 345 678",
      service_type: "Residential Electricity",
      customer_type: "residential",
      postcode: "2000",
      current_provider: "AGL",
      quarterly_bill: "450",
      has_solar: "No",
      preferred_contact_time: "Morning",
      notes: "Looking for a better deal",
    };
    let preview = htmlContent;
    Object.entries(sampleData).forEach(([key, val]) => {
      preview = preview.replaceAll(`{{${key}}}`, val);
    });
    return preview;
  };

  if (!templates) return <div className="py-8 text-center text-[#64748B]">Loading templates...</div>;

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <h3 className="font-bold text-[#0B203B] text-lg">Email Templates</h3>
      </div>

      {/* Template selector */}
      <div className="flex gap-2 mb-6">
        <Button
          variant={activeTemplate === "admin_notification" ? "default" : "outline"}
          onClick={() => switchTemplate("admin_notification")}
          className={activeTemplate === "admin_notification" ? "bg-[#0B203B] text-white" : ""}
          data-testid="template-admin-btn"
        >
          Admin Notification
        </Button>
        <Button
          variant={activeTemplate === "customer_confirmation" ? "default" : "outline"}
          onClick={() => switchTemplate("customer_confirmation")}
          className={activeTemplate === "customer_confirmation" ? "bg-[#0B203B] text-white" : ""}
          data-testid="template-customer-btn"
        >
          Customer Confirmation
        </Button>
      </div>

      <div className="bg-white rounded-xl border border-[#E2E8F0] p-6">
        {/* Subject */}
        <div className="mb-4">
          <label className="block text-sm font-semibold text-[#2E2E33] mb-1.5">Subject Line</label>
          <Input
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject..."
            data-testid="template-subject-input"
          />
          <p className="text-xs text-[#64748B] mt-1">Use {"{{full_name}}"}, {"{{service_type}}"}, {"{{email}}"} etc. as placeholders</p>
        </div>

        {/* HTML Editor */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-semibold text-[#2E2E33]">HTML Content</label>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
              data-testid="template-preview-btn"
            >
              <Eye className="w-4 h-4 mr-1" /> {showPreview ? "Edit" : "Preview"}
            </Button>
          </div>

          {showPreview ? (
            <div className="border border-[#E2E8F0] rounded-lg p-6 min-h-[300px] bg-white" data-testid="template-preview">
              <div dangerouslySetInnerHTML={{ __html: previewHtml() }} />
            </div>
          ) : (
            <textarea
              value={htmlContent}
              onChange={(e) => setHtmlContent(e.target.value)}
              className="w-full h-[300px] rounded-lg border border-[#E2E8F0] p-4 font-mono text-sm focus:ring-2 focus:ring-[#FF6600] focus:border-[#FF6600] outline-none resize-y"
              placeholder="<div>Your HTML email content...</div>"
              data-testid="template-html-editor"
            />
          )}
          <p className="text-xs text-[#64748B] mt-1">
            Available placeholders: {"{{full_name}}"}, {"{{email}}"}, {"{{phone}}"}, {"{{service_type}}"}, {"{{customer_type}}"}, {"{{postcode}}"}, {"{{current_provider}}"}, {"{{quarterly_bill}}"}, {"{{has_solar}}"}, {"{{preferred_contact_time}}"}, {"{{notes}}"}
          </p>
        </div>

        {/* Save */}
        <Button onClick={saveTemplate} disabled={saving} className="bg-[#FF6600] hover:bg-[#E55C00] text-white font-bold" data-testid="template-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Template
        </Button>
      </div>
    </div>
  );
}

function SettingsPanel() {
  const [settings, setSettings] = useState({ resend_api_key: "", sender_email: "", notification_email: "" });
  const [masked, setMasked] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings`, { headers: getAuthHeaders() }).then((r) => {
      setMasked(r.data.resend_api_key_masked || "");
      setSettings({
        resend_api_key: "",
        sender_email: r.data.sender_email || "",
        notification_email: r.data.notification_email || "",
      });
    }).catch(() => {});
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (settings.resend_api_key) payload.resend_api_key = settings.resend_api_key;
      if (settings.sender_email) payload.sender_email = settings.sender_email;
      if (settings.notification_email) payload.notification_email = settings.notification_email;
      await axios.put(`${API}/settings`, payload, { headers: getAuthHeaders() });
      toast.success("Settings saved successfully");
      if (settings.resend_api_key) {
        const key = settings.resend_api_key;
        setMasked(key.length > 10 ? key.slice(0, 6) + "..." + key.slice(-4) : "****");
        setSettings((s) => ({ ...s, resend_api_key: "" }));
      }
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl">
      <h3 className="font-bold text-[#0B203B] text-xl mb-6">Email Settings (Resend)</h3>
      <div className="space-y-5">
        <div>
          <label className="block text-sm font-semibold text-[#2E2E33] mb-1.5">Resend API Key</label>
          {masked && <p className="text-xs text-[#64748B] mb-1">Current: {masked}</p>}
          <Input
            type="password"
            placeholder="re_xxxxxxxx (leave blank to keep current)"
            value={settings.resend_api_key}
            onChange={(e) => setSettings({ ...settings, resend_api_key: e.target.value })}
            data-testid="settings-api-key"
          />
          <p className="text-xs text-[#64748B] mt-1">Get your API key from <a href="https://resend.com/api-keys" target="_blank" rel="noreferrer" className="text-[#FF6600] underline">resend.com</a></p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#2E2E33] mb-1.5">Sender Email (From)</label>
          <Input
            type="email"
            placeholder="noreply@salescorpaustralia.com.au"
            value={settings.sender_email}
            onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
            data-testid="settings-sender-email"
          />
          <p className="text-xs text-[#64748B] mt-1">Must be a verified domain in Resend</p>
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#2E2E33] mb-1.5">Notification Email (To)</label>
          <Input
            type="email"
            placeholder="hello@salescorpaustralia.com.au"
            value={settings.notification_email}
            onChange={(e) => setSettings({ ...settings, notification_email: e.target.value })}
            data-testid="settings-notification-email"
          />
          <p className="text-xs text-[#64748B] mt-1">Where lead notifications will be sent</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-[#0B203B] hover:bg-[#1E3A5F] text-white font-bold" data-testid="settings-save-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
          Save Settings
        </Button>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [stats, setStats] = useState({ total_leads: 0, new_leads: 0, contacted: 0, quoted: 0, won: 0, lost: 0, total_contacts: 0 });
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    loadData();
  }, [token, navigate]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      const [leadsRes, contactsRes, statsRes] = await Promise.all([
        axios.get(`${API}/leads`, { headers }),
        axios.get(`${API}/contacts`, { headers }),
        axios.get(`${API}/stats`, { headers }),
      ]);
      setLeads(leadsRes.data);
      setContacts(contactsRes.data);
      setStats(statsRes.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("admin_token");
        navigate("/admin/login");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleStatusChange = async (leadId, status) => {
    try {
      await axios.put(`${API}/leads/${leadId}`, { status }, { headers: getAuthHeaders() });
      setLeads((prev) => prev.map((l) => (l.id === leadId ? { ...l, status } : l)));
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update status");
    }
  };

  const handleDelete = async (leadId) => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await axios.delete(`${API}/leads/${leadId}`, { headers: getAuthHeaders() });
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
      toast.success("Lead deleted");
    } catch {
      toast.error("Failed to delete lead");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#E2E8F0] px-4 sm:px-6 lg:px-8 py-3 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-[#0a0a0a] rounded-lg px-3 py-1.5">
              <span className="text-white text-sm font-extrabold">SalesCorp</span>
            </div>
            <span className="text-sm font-semibold text-[#2E2E33]">Admin Dashboard</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="admin-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <StatsCards stats={stats} />

        <Tabs defaultValue="leads" className="space-y-6" data-testid="admin-tabs">
          <TabsList className="bg-white border border-[#E2E8F0] rounded-lg p-1">
            <TabsTrigger value="leads" className="data-[state=active]:bg-[#0B203B] data-[state=active]:text-white rounded-md px-4" data-testid="tab-leads">
              <Users className="w-4 h-4 mr-1.5" /> Leads
            </TabsTrigger>
            <TabsTrigger value="contacts" className="data-[state=active]:bg-[#0B203B] data-[state=active]:text-white rounded-md px-4" data-testid="tab-contacts">
              <MessageSquare className="w-4 h-4 mr-1.5" /> Contacts
            </TabsTrigger>
            <TabsTrigger value="email-logs" className="data-[state=active]:bg-[#0B203B] data-[state=active]:text-white rounded-md px-4" data-testid="tab-email-logs">
              <Mail className="w-4 h-4 mr-1.5" /> Email Log
            </TabsTrigger>
            <TabsTrigger value="templates" className="data-[state=active]:bg-[#0B203B] data-[state=active]:text-white rounded-md px-4" data-testid="tab-templates">
              <Code className="w-4 h-4 mr-1.5" /> Templates
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-[#0B203B] data-[state=active]:text-white rounded-md px-4" data-testid="tab-settings">
              <Settings className="w-4 h-4 mr-1.5" /> Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leads">
            <LeadsTable leads={leads} onStatusChange={handleStatusChange} onDelete={handleDelete} onRefresh={loadData} loading={loading} />
          </TabsContent>
          <TabsContent value="contacts">
            <ContactsTable contacts={contacts} />
          </TabsContent>
          <TabsContent value="email-logs">
            <EmailLogsPanel />
          </TabsContent>
          <TabsContent value="templates">
            <EmailTemplatesPanel />
          </TabsContent>
          <TabsContent value="settings">
            <SettingsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
